import { useCallback, useEffect, useState } from 'react';
import { notificationService } from '../services/api/notifications';
import { UserNotification } from '../types/notifications';

interface FetchNotificationListOptions {
  limit?: number;
  offset?: number;
  append?: boolean;
}

const hasAuthToken = () => {
  return Boolean(
    localStorage.getItem('token') || sessionStorage.getItem('token'),
  );
};

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    if (!hasAuthToken()) {
      setUnreadCount(0);
      return;
    }

    try {
      const response = await notificationService.unreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread notification count:', error);
    }
  }, []);

  const fetchList = useCallback(async (options: FetchNotificationListOptions = {}) => {
    if (!hasAuthToken()) {
      setNotifications([]);
      return [];
    }

    const { limit = 20, offset = 0, append = false } = options;
    setIsLoading(true);
    try {
      const response = await notificationService.list(limit, offset);
      setNotifications((current) =>
        append ? [...current, ...response.data] : response.data,
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const markRead = useCallback(
    async (id: number) => {
      const previousNotifications = notifications;
      const previousCount = unreadCount;
      const target = previousNotifications.find((item) => item.id === id);
      const shouldDecrement = Boolean(target && !target.isRead);

      setNotifications((current) =>
        current.map((item) =>
          item.id === id ? { ...item, isRead: true } : item,
        ),
      );
      if (shouldDecrement) {
        setUnreadCount((current) => Math.max(0, current - 1));
      }

      try {
        await notificationService.markRead(id);
      } catch (error) {
        setNotifications(previousNotifications);
        setUnreadCount(previousCount);
        console.error('Failed to mark notification as read:', error);
      }
    },
    [notifications, unreadCount],
  );

  const markAllRead = useCallback(async () => {
    const previousNotifications = notifications;
    const previousCount = unreadCount;

    setNotifications((current) =>
      current.map((item) => ({ ...item, isRead: true })),
    );
    setUnreadCount(0);

    try {
      await notificationService.markAllRead();
    } catch (error) {
      setNotifications(previousNotifications);
      setUnreadCount(previousCount);
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [notifications, unreadCount]);

  useEffect(() => {
    void Promise.resolve().then(fetchUnreadCount);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    }, 30_000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    };

    window.addEventListener('focus', fetchUnreadCount);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', fetchUnreadCount);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    fetchList,
    markRead,
    markAllRead,
  };
};
